import classNames from 'classnames';
import { AnimatePresence } from 'motion/react';
import React, { useEffect, useState } from 'react';
import { useEffectEvent } from 'use-effect-event';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import CopyLinkButton from '../../components/CopyLinkButton';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import Tooltip from '../../components/Tooltip';
import PopOverContainer from '../../components/deprecated/PopOverContainer';
import { EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import editIcon from '../../icons/non-prefilled/16x16/editPost.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { miscToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import addTestId from '../../utils/addTestId';
import {
  PostLayoutSize,
  useIsPostLayoutSize,
} from '../../utils/responsive/PostLayoutSizeProvider';
import useRemoveHash from '../../utils/router/useRemoveHash';
import annotationThreadHash from '../../utils/url/annotationThreadHash';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import {
  ANNOTATION_CREATION_ID,
  usePendingMagneticWallItem,
} from '../PendingMagneticWallItemProvider';
import MagneticWallItemOverflowMenu, {
  MagneticWallOverflowButton,
  isClickingMagneticWallItemOverflowButton,
} from '../comment/MagneticWallItemOverflowMenu';
import type { CompactPopOverSide } from '../comment/getMagneticWallItemLayout';
import { useOptimisticItemContentRects } from '../useOptimisticItemContentRects';
import useShareSecret from '../useShareSecret';
import isContentEmpty from '../utils/isContentEmpty';
import * as styles from './Annotation.module.scss';
import AnnotationCreate from './AnnotationCreate';
import AnnotationIcon from './AnnotationIcon';
import AnnotationLink from './AnnotationLink';
import AnnotationPreview from './AnnotationPreview';
import useAnnotationState from './useAnnotationState';

const POST_FRAGMENT = gql(`
  fragment Annotation_post on RootPostFields {
    id
    ...postPath_post
  }
`);

const ANNOTATION_FRAGMENT = gql(`
  fragment Annotation_annotation on Annotation {
    id
    ...AnnotationPreview_annotation
    ...useAnnotationState_annotation
  }
`);

interface AnnotationCompactProps {
  onClick: () => void;
  active: boolean;
}

const AnnotationCompact = ({ onClick, active }: AnnotationCompactProps) => {
  const isTablet = useIsPostLayoutSize(PostLayoutSize.tablet);

  return (
    <div
      {...addTestId('annotation-icon')}
      className={classNames({
        [styles.compactAnnotationIconContainer]: !isTablet,
      })}
      onClick={onClick}
    >
      {isTablet ? (
        <AnnotationIcon size="small" />
      ) : (
        <AnnotationIcon hoverable={!active} />
      )}
    </div>
  );
};

interface AnnotationFullIconContainerProps {
  children: React.ReactNode;
  onClick: () => void;
}

const AnnotationFullIconContainer = ({
  children,
  onClick,
}: AnnotationFullIconContainerProps) => {
  return (
    <div className={styles.fullIconContainer}>
      <div
        className={styles.icon}
        {...addTestId('annotation-icon')}
        onClick={onClick}
      >
        {children}
      </div>
    </div>
  );
};

interface AnnotationPreviewContainerProps {
  children: React.ReactNode;
  onClick?: () => void;
}

export const AnnotationPreviewContainer = ({
  children,
  onClick,
}: AnnotationPreviewContainerProps) => {
  return (
    <div className={styles.previewContainer}>
      <div onClick={onClick} className={styles.preview}>
        {children}
      </div>
    </div>
  );
};

interface AnnotationProps {
  isFocused: boolean;
  annotation: DocumentType<typeof ANNOTATION_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  onResize: (avoidRepositioningFocusedThread?: boolean) => void;
  popOverOpen: boolean;
  overlapped: boolean;
  size: MagneticWallItemSize;
  canEdit: boolean;
  compactPopOverSide: CompactPopOverSide;
  createAnnotation: React.ComponentProps<
    typeof AnnotationCreate
  >['createAnnotation'];
  deleteAnnotation: () => void;
  onPopOverHeightChange: (height: number) => void;
}

const Annotation = ({
  isFocused,
  annotation,
  post,
  onResize,
  popOverOpen,
  overlapped,
  size,
  canEdit,
  compactPopOverSide,
  createAnnotation,
  deleteAnnotation,
  onPopOverHeightChange,
}: AnnotationProps) => {
  const shareSecret = useShareSecret();
  const isNewAnnotation = annotation.id === ANNOTATION_CREATION_ID;
  const dispatch = useAppDispatch();

  const [isHovered, setIsHovered] = useState(false);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  if (!isFocused && isOverflowMenuOpen) {
    setIsOverflowMenuOpen(false);
  }

  const optimisticItemContentRects = useOptimisticItemContentRects();
  const { setPendingAnnotationRange } = usePendingMagneticWallItem();

  const {
    annotationState,
    isEditing,
    updateAnnotationState,
    cancelEditing,
    startEditing,
    finishEditing,
  } = useAnnotationState(annotation);
  const trimmedLinkText = annotationState.linkText.trim();
  const sanitizedLink = sanitizeAnnotationLink(annotationState.link);

  useEffect(() => {
    onResize();
  }, [onResize, isFocused]);

  const cancelEditingEvent = useEffectEvent(cancelEditing);

  useEffect(() => {
    if (
      !isFocused &&
      isEditing &&
      ((isNewAnnotation &&
        isContentEmpty(annotationState.content) &&
        annotationState.linkText === '' &&
        annotationState.link === '') ||
        size === 'compact')
    ) {
      cancelEditingEvent();
      setPendingAnnotationRange(null);
    }
  }, [
    isFocused,
    isEditing,
    isNewAnnotation,
    annotationState.content,
    annotationState.linkText,
    annotationState.link,
    setPendingAnnotationRange,
    size,
  ]);

  const handleDismiss = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const focusAnnotationById = (id: string) => {
    dispatch(
      EditorActions.focusMagneticWall({
        id,
        type: 'annotation',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
  };

  const focusCurrentAnnotation = () => focusAnnotationById(annotation.id);

  const removeHash = useRemoveHash();

  const handleDelete = () => {
    deleteAnnotation();

    if (window.location.hash === annotationThreadHash(annotation.id)) {
      removeHash();
    }
    handleDismiss();
  };

  const handleClick = (e: React.MouseEvent) => {
    if (
      !popOverOpen &&
      !isClickingMagneticWallItemOverflowButton(e.nativeEvent)
    ) {
      dispatch(
        EditorActions.focusMagneticWall({
          id: annotation.id,
          type: 'annotation',
          isActive: true,
          hasExpandIntent: true,
        }),
      );
    }
  };

  const shouldShowLink =
    (isEditing || !isContentEmpty(annotation.content)) && sanitizedLink;

  const isExpanded = isFocused || isEditing;

  const copyAnnotationLink = () => {
    const url = fullOrgUrl(
      postPath(post, {
        shareSecret,
        anchor: annotationThreadHash(annotation.id),
      }),
    );

    return navigator.clipboard.writeText(url);
  };

  const innerFullView = (
    <div className={styles.innerFullViewContainer}>
      {isEditing ? (
        <div className={styles.createContainer}>
          <AnnotationCreate
            annotationId={annotation.id}
            annotationState={annotationState}
            updateAnnotationState={updateAnnotationState}
            onSaved={finishEditing}
            onCancel={cancelEditing}
            createAnnotation={createAnnotation}
          />
        </div>
      ) : (
        <AnnotationPreviewContainer onClick={focusCurrentAnnotation}>
          <AnnotationPreview
            annotation={{
              id: annotation.id,
              ...annotationState,
            }}
            avatar={
              <CopyLinkButton onCopyLink={copyAnnotationLink}>
                <AnnotationIcon />
              </CopyLinkButton>
            }
          />
        </AnnotationPreviewContainer>
      )}
      {(isHovered || isOverflowMenuOpen) &&
        !isEditing &&
        !optimisticItemContentRects.has(annotation.id) && (
          <MagneticWallItemOverflowMenu
            open={isOverflowMenuOpen}
            onOpenChange={setIsOverflowMenuOpen}
            menuItems={
              canEdit
                ? [
                    <MenuItem
                      icon={editIcon}
                      key="edit"
                      onClick={() => {
                        focusCurrentAnnotation();
                        startEditing();
                      }}
                      testId="edit-annotation-button"
                    >
                      Edit annotation
                    </MenuItem>,
                    <MenuSeparator key="separator" />,
                    <MenuItem
                      icon={trashIcon}
                      key="delete"
                      danger
                      onClick={handleDelete}
                    >
                      Delete annotation
                    </MenuItem>,
                  ]
                : []
            }
          >
            <Tooltip content={`Copy link\nto annotation`}>
              <MagneticWallOverflowButton
                onClick={() =>
                  copyAnnotationLink().then(() => {
                    dispatch(UIActions.pushToast(miscToasts.linkCopied));
                  })
                }
                icon={linkIcon}
              />
            </Tooltip>
          </MagneticWallItemOverflowMenu>
        )}
    </div>
  );

  return (
    <MagneticWallItem.Root
      focused={isExpanded}
      onMouseEnter={() => {
        dispatch(EditorActions.highlightAnnotation(annotation.id));
      }}
      onMouseLeave={() => {
        dispatch(EditorActions.removeMagneticWallHighlight());
      }}
      onClick={handleClick}
      id={`annotation-${annotation.id}`}
      fullView={
        <MagneticWallItem.Card
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          focused={isExpanded}
          overlapped={overlapped}
        >
          <AnimatePresence>
            {isExpanded ? (
              <>{innerFullView}</>
            ) : (
              <AnnotationFullIconContainer
                onClick={() => focusCurrentAnnotation()}
              >
                <AnnotationIcon hoverable />
              </AnnotationFullIconContainer>
            )}
            {isExpanded && shouldShowLink && (
              <OpacityMotionTransition
                key="link"
                disabled={isEditing}
                transition={{ duration: 0.25 }}
              >
                <AnnotationLink link={sanitizedLink} text={trimmedLinkText} />
              </OpacityMotionTransition>
            )}
          </AnimatePresence>
        </MagneticWallItem.Card>
      }
      compactView={
        <MagneticWallItem.ResponsivePopover
          onClose={handleDismiss}
          target={
            <AnnotationCompact
              active={isExpanded}
              onClick={() => focusCurrentAnnotation()}
            />
          }
          onPopOverHeightChange={onPopOverHeightChange}
          content={
            <PopOverContainer noPadding>
              <div
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {innerFullView}
                {shouldShowLink && (
                  <div className={styles.annotationLinkContainer}>
                    <AnnotationLink
                      link={sanitizedLink}
                      text={trimmedLinkText}
                    />
                  </div>
                )}
              </div>
            </PopOverContainer>
          }
          compactPopOverSide={compactPopOverSide}
          open={popOverOpen}
        />
      }
      size={size}
    />
  );
};

export default Annotation;
